import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Search, RefreshCw, X, Workflow, Bot, ScrollText, ChevronRight, Share2, Check, Copy } from "lucide-react";
import JsonViewer from "@/components/JsonViewer";

const SOURCE_META = {
    audit: { label: "Audit", icon: ScrollText, tone: "text-amber-300 border-amber-300/30 bg-amber-300/10" },
    flow:  { label: "Flow",  icon: Workflow,   tone: "text-[#7C3AED] border-[#7C3AED]/40 bg-[#7C3AED]/15" },
    agent: { label: "Agent", icon: Bot,        tone: "text-emerald-300 border-emerald-300/30 bg-emerald-300/10" },
};

const STATUS_TONE = {
    completed: "text-emerald-300",
    failed: "text-red-300",
    running: "text-amber-300",
    awaiting_approval: "text-amber-200",
    cancelled: "text-white/40",
};

function fmtTs(ts) {
    if (!ts) return "—";
    try {
        return new Date(ts).toLocaleString();
    } catch {
        return String(ts);
    }
}

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function ObservabilityPage() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        actor_email: params.get("actor_email") || "",
        action: params.get("action") || "",
        request_id: params.get("request_id") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
    });
    const [sources, setSources] = useState({
        audit: params.get("audit") !== "0",
        flow: params.get("flow") !== "0",
        agent: params.get("agent") !== "0",
    });
    const [traceFor, setTraceFor] = useState(params.get("trace") || null);

    const activeSources = useMemo(
        () => Object.entries(sources).filter(([, v]) => v).map(([k]) => k).join(",") || "audit,flow,agent",
        [sources],
    );

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
            q.set("sources", activeSources);
            q.set("limit", "200");
            const { data } = await api.get(`/admin/observability/feed?${q.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [activeSources]);

    const applyFilters = (e) => {
        e?.preventDefault();
        // sync filters to URL for shareable links
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) next.set(k, v); });
        Object.entries(sources).forEach(([k, v]) => { if (!v) next.set(k, "0"); });
        if (traceFor) next.set("trace", traceFor);
        setParams(next, { replace: true });
        load();
    };

    const clearFilters = () => {
        setFilters({ actor_email: "", action: "", request_id: "", date_from: "", date_to: "" });
        setSources({ audit: true, flow: true, agent: true });
        setParams({}, { replace: true });
    };

    const counts = useMemo(() => {
        const c = { audit: 0, flow: 0, agent: 0 };
        for (const it of items) c[it.source] = (c[it.source] || 0) + 1;
        return c;
    }, [items]);

    return (
        <div className="px-8 py-8" data-testid="observability-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Observability</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2 flex items-center gap-3">
                        Execution Feed
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border border-white/15 text-white/60 rounded-sm font-mono">
                            {items.length} rows
                        </span>
                    </h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Unified view of audit log, flow runs, and agent runs. Click any row to walk
                        the full execution tree and inspect rendered prompts + per-node latency &amp; cost.
                    </p>
                </div>
                <button
                    onClick={load}
                    data-testid="obs-refresh"
                    className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" strokeWidth={1.6} /> Refresh
                </button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                {(["audit", "flow", "agent"]).map((s) => {
                    const meta = SOURCE_META[s];
                    const Icon = meta.icon;
                    return (
                        <div
                            key={s}
                            data-testid={`obs-kpi-${s}`}
                            className={`bg-[#0a0a0a] border rounded-md p-4 cursor-pointer transition-colors ${sources[s] ? "border-white/30" : "border-white/10 opacity-60"}`}
                            onClick={() => setSources((p) => ({ ...p, [s]: !p[s] }))}
                            title={sources[s] ? "Showing — click to hide" : "Hidden — click to show"}
                        >
                            <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" strokeWidth={1.6} />
                                <span className="label-xs">{meta.label} entries</span>
                            </div>
                            <div className="font-display text-2xl mt-2">{counts[s] || 0}</div>
                            <div className="text-[10px] uppercase tracking-wider mt-1 text-white/40 font-mono">
                                {sources[s] ? "visible" : "hidden — click to show"}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Filters */}
            <form onSubmit={applyFilters} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-5">
                <div className="grid grid-cols-5 gap-3">
                    <FilterInput label="Actor email" value={filters.actor_email} onChange={(v) => setFilters((p) => ({ ...p, actor_email: v }))} testId="obs-filter-actor" />
                    <FilterInput label="Action contains" value={filters.action} onChange={(v) => setFilters((p) => ({ ...p, action: v }))} testId="obs-filter-action" placeholder="e.g. flow.publish" />
                    <FilterInput label="Request ID" value={filters.request_id} onChange={(v) => setFilters((p) => ({ ...p, request_id: v }))} testId="obs-filter-request" placeholder="flow_run_id / agent_run_id" />
                    <FilterInput label="From (ISO)" value={filters.date_from} onChange={(v) => setFilters((p) => ({ ...p, date_from: v }))} testId="obs-filter-from" placeholder="2026-02-01" />
                    <FilterInput label="To (ISO)" value={filters.date_to} onChange={(v) => setFilters((p) => ({ ...p, date_to: v }))} testId="obs-filter-to" placeholder="2026-02-28" />
                </div>
                <div className="flex justify-end gap-2 mt-3">
                    <button type="button" onClick={clearFilters} data-testid="obs-clear" className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Clear</button>
                    <button type="submit" data-testid="obs-apply" className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 flex items-center gap-2">
                        <Search className="w-4 h-4" strokeWidth={1.8} /> Apply
                    </button>
                </div>
            </form>

            {/* Feed */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3">Time</th>
                            <th className="label-xs px-5 py-3">Source</th>
                            <th className="label-xs px-5 py-3">Kind</th>
                            <th className="label-xs px-5 py-3">Actor</th>
                            <th className="label-xs px-5 py-3">Summary</th>
                            <th className="label-xs px-5 py-3 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={6} className="px-5 py-16 text-center">
                                <Activity className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                <div className="text-sm text-white/60">No entries match these filters</div>
                            </td></tr>
                        ) : items.map((it, idx) => {
                            const meta = SOURCE_META[it.source] || SOURCE_META.audit;
                            const Icon = meta.icon;
                            const traceable = it.source === "flow" || it.source === "agent";
                            // Audit rows often share the same `resource_id` across actions, so include
                            // the audit doc's own id (raw.id) AND idx for guaranteed uniqueness.
                            const rowKey = `${it.source}-${it.raw?.id || it.resource_id || "x"}-${idx}`;
                            return (
                                <tr
                                    key={rowKey}
                                    data-testid={`obs-row-${idx}`}
                                    className={`data-row border-t border-white/5 ${traceable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
                                    onClick={() => traceable && setTraceFor(it.resource_id)}
                                >
                                    <td className="px-5 py-3 font-mono text-xs text-white/60 whitespace-nowrap">{fmtTs(it.timestamp)}</td>
                                    <td className="px-5 py-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${meta.tone}`}>
                                            <Icon className="w-3 h-3" strokeWidth={2} />
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        <span className={STATUS_TONE[(it.kind || "").split(".").pop()] || "text-white/80"}>
                                            {it.kind || "—"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs font-mono text-white/70 truncate max-w-[200px]">{it.actor || "—"}</td>
                                    <td className="px-5 py-3 text-sm text-white/85 truncate max-w-[440px]">{it.summary}</td>
                                    <td className="px-5 py-3 text-right">
                                        {traceable && <ChevronRight className="w-4 h-4 text-white/30" strokeWidth={1.6} />}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {traceFor && <TraceModal requestId={traceFor} onClose={() => setTraceFor(null)} />}
        </div>
    );
}

function FilterInput({ label, value, onChange, testId, placeholder }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || ""}
                data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30"
            />
        </div>
    );
}

function TraceModal({ requestId, onClose }) {
    const [trace, setTrace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeNode, setActiveNode] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/observability/trace/${requestId}`);
                setTrace(data);
            } catch (e) {
                setError(formatApiErrorDetail(e?.response?.data?.detail));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [requestId]);

    const isFlow = trace?.kind === "flow_run";
    const fr = trace?.flow_run;
    const ar = trace?.agent_run;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div
                className="w-full max-w-5xl max-h-[90vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="obs-trace-modal"
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
                    <div className="min-w-0">
                        <div className="font-display text-lg tracking-tight">Execution trace</div>
                        <div className="text-[11px] text-white/50 font-mono truncate">{requestId}</div>
                    </div>
                    <button
                        onClick={() => setShareOpen(true)}
                        data-testid="obs-trace-share"
                        className="ml-auto flex items-center gap-1.5 border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] hover:bg-[#7C3AED]/20 px-2.5 py-1.5 rounded-md text-xs"
                    >
                        <Share2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Share
                    </button>
                    <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="obs-trace-close">
                        <X className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                </div>
                {shareOpen && (
                    <ShareTraceModal
                        requestId={requestId}
                        onClose={() => setShareOpen(false)}
                    />
                )}
                <div className="flex-1 overflow-auto">
                    {loading && <div className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading trace…</div>}
                    {error && <div className="px-5 py-8 text-center text-xs text-red-400 font-mono" data-testid="obs-trace-error">{error}</div>}

                    {!loading && !error && isFlow && (
                        <div className="grid grid-cols-2 gap-0">
                            {/* Left: nodes */}
                            <div className="border-r border-white/10 p-5 space-y-3 overflow-auto" style={{ maxHeight: "70vh" }}>
                                <div className="space-y-1">
                                    <div className="label-xs text-white/40">Flow run</div>
                                    <div className="font-display text-base">{fr?.flow_slug} v{fr?.version_no}</div>
                                    <div className="text-xs text-white/60 font-mono">
                                        status={" "}
                                        <span className={STATUS_TONE[fr?.status] || ""}>{fr?.status}</span>
                                        {" · "}env={fr?.environment || "—"}
                                        {" · "}cost=${(fr?.totals?.cost_usd || 0).toFixed(5)}
                                        {" · "}latency={fr?.totals?.latency_ms || 0}ms
                                    </div>
                                </div>
                                <div className="border-t border-white/10 pt-3 space-y-1.5">
                                    <div className="label-xs text-white/40 mb-1">Node executions ({trace.node_executions?.length || 0})</div>
                                    {(trace.node_executions || []).map((ne) => (
                                        <button
                                            key={ne.id}
                                            onClick={() => setActiveNode(ne)}
                                            data-testid={`obs-node-${ne.node_id}`}
                                            className={`w-full text-left p-2.5 border rounded-md transition-colors ${activeNode?.id === ne.id ? "border-[#7C3AED]/60 bg-[#7C3AED]/10" : "border-white/10 hover:bg-white/5"}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-mono text-white/85">{ne.node_id}</span>
                                                <span className={`text-[10px] uppercase tracking-wider font-mono ${STATUS_TONE[ne.status] || "text-white/40"}`}>{ne.status}</span>
                                            </div>
                                            <div className="text-[11px] text-white/40 mt-1 font-mono">
                                                {ne.node_type} · {ne.latency_ms || 0}ms · ${(ne.cost_usd || 0).toFixed(5)}
                                            </div>
                                            {ne.agent_run_id && (
                                                <div className="text-[10px] text-emerald-300 font-mono mt-0.5">→ agent_run {ne.agent_run_id.slice(0, 8)}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Right: detail */}
                            <div className="p-5 overflow-auto" style={{ maxHeight: "70vh" }}>
                                {!activeNode ? (
                                    <div className="space-y-3">
                                        <div className="label-xs text-white/40">Run input</div>
                                        <JsonViewer data={fr?.input_payload || {}} maxHeight={200} testId="obs-trace-input" />
                                        <div className="label-xs text-white/40 mt-3">Run output</div>
                                        <JsonViewer data={fr?.output_payload || {}} maxHeight={200} testId="obs-trace-output" />
                                    </div>
                                ) : (
                                    <div className="space-y-3" data-testid="obs-node-detail">
                                        <div>
                                            <div className="label-xs text-white/40">Node</div>
                                            <div className="font-display text-base">{activeNode.node_id} <span className="text-white/40 text-sm font-mono">({activeNode.node_type})</span></div>
                                        </div>
                                        <div>
                                            <div className="label-xs text-white/40 mb-1">Input</div>
                                            <JsonViewer data={activeNode.input_payload || {}} maxHeight={160} />
                                        </div>
                                        <div>
                                            <div className="label-xs text-white/40 mb-1">Output</div>
                                            <JsonViewer data={activeNode.output_payload || {}} maxHeight={160} />
                                        </div>
                                        {activeNode.error && (
                                            <div className="text-xs font-mono text-red-300 border border-red-300/30 bg-red-300/10 rounded p-2 whitespace-pre-wrap" data-testid="obs-node-error">
                                                {activeNode.error}
                                            </div>
                                        )}
                                        {activeNode.agent_run && (
                                            <div className="border-t border-white/10 pt-3">
                                                <div className="label-xs text-emerald-300 mb-1">Linked agent_run</div>
                                                <div className="text-xs font-mono text-white/70 mb-2">
                                                    {activeNode.agent_run.agent_slug} v{activeNode.agent_run.version_no} · {activeNode.agent_run.status}
                                                </div>
                                                <div className="label-xs text-white/40 mb-1">Rendered system prompt</div>
                                                <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                                    {activeNode.agent_run.rendered_system_prompt || "(no system prompt)"}
                                                </pre>
                                                <div className="label-xs text-white/40 mb-1 mt-2">Rendered user prompt</div>
                                                <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                                    {activeNode.agent_run.rendered_user_prompt || "(no user prompt)"}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!loading && !error && !isFlow && ar && (
                        <div className="p-5 space-y-3">
                            <div>
                                <div className="label-xs text-white/40">Agent run</div>
                                <div className="font-display text-base">{ar.agent_slug} v{ar.version_no}</div>
                                <div className="text-xs text-white/60 font-mono">
                                    status=<span className={STATUS_TONE[ar.status] || ""}>{ar.status}</span>
                                    {" · "}cost=${(ar.total_cost_usd || 0).toFixed(5)}
                                    {" · "}latency={ar.latency_ms || 0}ms
                                </div>
                            </div>
                            <div>
                                <div className="label-xs text-white/40 mb-1">Input</div>
                                <JsonViewer data={ar.input_payload || {}} maxHeight={160} />
                            </div>
                            <div>
                                <div className="label-xs text-white/40 mb-1">Output</div>
                                <JsonViewer data={ar.output_payload || {}} maxHeight={160} />
                            </div>
                            {ar.error && (
                                <div className="text-xs font-mono text-red-300 border border-red-300/30 bg-red-300/10 rounded p-2 whitespace-pre-wrap">
                                    {ar.error}
                                </div>
                            )}
                            <div className="label-xs text-white/40 mb-1">Rendered system prompt</div>
                            <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                {ar.rendered_system_prompt || "(no system prompt)"}
                            </pre>
                            <div className="label-xs text-white/40 mb-1">Rendered user prompt</div>
                            <pre className="text-[11px] font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-2 max-h-40 overflow-auto">
                                {ar.rendered_user_prompt || "(no user prompt)"}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function ShareTraceModal({ requestId, onClose }) {
    const [ttl, setTtl] = useState(7 * 24 * 3600); // 7 days default
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const mint = async (ttlSeconds) => {
        setLoading(true);
        try {
            const { data } = await api.post(
                `/admin/observability/trace/${requestId}/share`,
                { ttl_seconds: ttlSeconds },
            );
            setResult(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mint(ttl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const publicUrl = result
        ? `${window.location.origin}${result.path}`
        : "";

    const copy = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't access clipboard — copy manually");
        }
    };

    const regenerate = (newTtl) => {
        setTtl(newTtl);
        setResult(null);
        mint(newTtl);
    };

    const TTL_OPTIONS = [
        { s: 60 * 60, label: "1 hour" },
        { s: 24 * 3600, label: "24 hours" },
        { s: 7 * 24 * 3600, label: "7 days" },
        { s: 30 * 24 * 3600, label: "30 days" },
    ];

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 rounded-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="obs-trace-share-modal"
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div>
                        <div className="font-display text-lg tracking-tight">Share trace</div>
                        <div className="text-[11px] text-white/50 font-mono">{requestId}</div>
                    </div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white" data-testid="obs-trace-share-close">
                        <X className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="text-xs text-white/70 leading-relaxed">
                        Anyone with this link can view the execution trace — inputs, outputs, rendered prompts,
                        and per-node latency / cost. Actor identifiers are stripped from the public payload.
                        The link expires automatically.
                    </div>

                    <div>
                        <div className="label-xs mb-2">Expires in</div>
                        <div className="flex gap-1.5 flex-wrap">
                            {TTL_OPTIONS.map((opt) => (
                                <button
                                    key={opt.s}
                                    onClick={() => regenerate(opt.s)}
                                    data-testid={`obs-trace-share-ttl-${opt.s}`}
                                    className={`px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                                        ttl === opt.s
                                            ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-white"
                                            : "border-white/15 text-white/70 hover:bg-white/5"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="label-xs mb-1.5">Public URL</div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                readOnly
                                value={loading ? "Generating…" : publicUrl}
                                data-testid="obs-trace-share-url"
                                className="flex-1 bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-white/30 text-white/85"
                                onFocus={(e) => e.target.select()}
                            />
                            <button
                                onClick={copy}
                                disabled={!publicUrl}
                                data-testid="obs-trace-share-copy"
                                className="border border-[#7C3AED]/40 bg-[#7C3AED]/15 text-white hover:bg-[#7C3AED]/25 px-3 py-2 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-40"
                            >
                                {copied ? (<><Check className="w-3.5 h-3.5" /> Copied</>) : (<><Copy className="w-3.5 h-3.5" /> Copy</>)}
                            </button>
                        </div>
                    </div>

                    {result && (
                        <div className="text-[11px] font-mono text-white/50 flex items-center justify-between border-t border-white/10 pt-3">
                            <span data-testid="obs-trace-share-expires">
                                Expires: <span className="text-white/75">{new Date(result.expires_at).toLocaleString()}</span>
                            </span>
                            <span>
                                Kind: <span className="text-white/75">{result.resource_kind}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
